import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadToCloudinary } from "@/lib/media";
import { useAuth } from "@/context/AuthContext";

const SIZE = 800;

function squareCrop(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const out = Math.min(SIZE, side);
      const c = document.createElement("canvas");
      c.width = out; c.height = out;
      const ctx = c.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, out, out);
      URL.revokeObjectURL(img.src);
      c.toBlob((b) => (b ? resolve(new File([b], "avatar.jpg", { type: "image/jpeg" })) : reject(new Error("Falha ao processar a imagem"))), "image/jpeg", 0.92);
    };
    img.onerror = () => reject(new Error("Formato de imagem não suportado"));
    img.src = URL.createObjectURL(file);
  });
}

export function AvatarUploader({ onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const { refreshMe } = useAuth();

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Escolha uma imagem");
    setBusy(true); setPct(0);
    try {
      const media = await uploadToCloudinary(await squareCrop(file), "photo", setPct);
      const { data } = await api.post("/users/me/avatar", { public_id: media.public_id });
      onChange(data.avatar);
      refreshMe();
      toast.success("Foto de perfil atualizada");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.guard) toast.error("Foto recusada pelo Guarda-Costas IA", { description: d.msg, duration: 8000 });
      else toast.error(err?.response ? formatApiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => ref.current.click()} disabled={busy} aria-label="Alterar foto de perfil" data-testid="profile-avatar-upload-button"
        className="absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full bg-[hsl(var(--gold))] text-[#0B0F17] shadow-lg ring-4 ring-background transition-transform active:scale-90">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
      </button>
      {busy && <span className="absolute inset-0 grid place-items-center rounded-full bg-black/60 text-xs font-bold" data-testid="profile-avatar-upload-progress">{pct}%</span>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="profile-avatar-file-input" />
    </>
  );
}
